import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Coins, Users, AlertTriangle, CheckCircle2, XCircle, TrendingUp, Gift } from "lucide-react";
import { adminApi } from "../../api/auth";

const fmt = (n, c) => {
  if (c === "USD") return `US$ ${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  return `${Number(n).toLocaleString("es-AR")} ${c || ""}`.trim();
};
const MET = { paypal: "PayPal", mp_ar: "MercadoPago AR", mp_cl: "MercadoPago CL", khipu: "Khipu", crypto: "USDT" };

const StatCard = ({ icon: Icon, label, value, sub, color = "#d4a843", testId }) => (
  <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-[#b8b3a8] uppercase tracking-wider mb-2">
      <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
    </div>
    <div className="text-2xl font-semibold text-white">{value}</div>
    {sub && <div className="text-xs text-[#7e7768] mt-1">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminApi.stats().then(setData).catch((e) => setErr(e.message));
  }, []);

  if (err) {
    return <div className="text-red-400 text-sm p-4 border border-red-900/40 rounded">{err}</div>;
  }
  if (!data) {
    return <Loader2 className="w-6 h-6 animate-spin text-[#d4a843]" />;
  }

  const { totals, by_currency, by_method, today, this_month, top_donors, claims } = data;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <div className="ornament"><span>Dashboard</span></div>
        <h1 className="section-title mt-3 text-3xl mb-6">Resumen general</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={CheckCircle2} label="Donaciones aprobadas" value={totals.approved_orders} testId="stat-approved" />
        <StatCard icon={Coins} label="Coins acreditadas" value={totals.total_coins.toLocaleString()} color="#facc15" testId="stat-coins" />
        <StatCard icon={Users} label="Donantes unicos" value={totals.unique_donors} color="#60a5fa" testId="stat-donors" />
        <StatCard icon={AlertTriangle} label="Pendientes / Rechazadas" value={`${totals.pending} / ${totals.rejected}`} color="#f87171" testId="stat-pending" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
          <h3 className="text-sm text-[#d4a843] uppercase tracking-wider mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Total por moneda
          </h3>
          {by_currency.length === 0 && <div className="text-xs text-[#7e7768]">Sin datos</div>}
          <div className="space-y-2">
            {by_currency.map((r) => (
              <div key={r.currency} className="flex justify-between text-sm">
                <span className="text-[#b8b3a8]">{r.currency} <span className="text-[#7e7768]">({r.orders} ord.)</span></span>
                <span className="text-white font-medium">{fmt(r.total, r.currency)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
          <h3 className="text-sm text-[#d4a843] uppercase tracking-wider mb-3 flex items-center gap-2">
            <Gift className="w-4 h-4" /> Claims
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-[#7e7768]">Total:</span> <span className="text-white">{claims.total}</span></div>
            <div><span className="text-[#7e7768]">Entregadas:</span> <span className="text-green-400">{claims.claimed}</span></div>
            <div><span className="text-[#7e7768]">Pendientes:</span> <span className="text-amber-400">{claims.unclaimed}</span></div>
            <div><span className="text-[#7e7768]">Coins entregadas:</span> <span className="text-white">{claims.coins_delivered.toLocaleString()}</span></div>
            <div className="col-span-2 pt-2 border-t border-[#3a3328]">
              <span className="text-[#7e7768]">Coins pendientes de claim:</span> <span className="text-amber-400">{claims.coins_pending.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
          <h3 className="text-sm text-[#d4a843] uppercase tracking-wider mb-3">Hoy</h3>
          {today.length === 0 ? <div className="text-xs text-[#7e7768]">Sin movimientos hoy</div> :
            today.map((r) => (
              <div key={r.currency} className="flex justify-between text-sm py-1">
                <span className="text-[#b8b3a8]">{r.currency} <span className="text-[#7e7768]">({r.orders})</span></span>
                <span className="text-white">{fmt(r.total, r.currency)}</span>
              </div>
            ))}
        </div>
        <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
          <h3 className="text-sm text-[#d4a843] uppercase tracking-wider mb-3">Este mes</h3>
          {this_month.length === 0 ? <div className="text-xs text-[#7e7768]">Sin movimientos</div> :
            this_month.map((r) => (
              <div key={r.currency} className="flex justify-between text-sm py-1">
                <span className="text-[#b8b3a8]">{r.currency} <span className="text-[#7e7768]">({r.orders})</span></span>
                <span className="text-white">{fmt(r.total, r.currency)}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
        <h3 className="text-sm text-[#d4a843] uppercase tracking-wider mb-3">Por metodo (aprobadas)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#7e7768] text-xs uppercase border-b border-[#3a3328]">
                <th className="text-left py-2">Metodo</th>
                <th className="text-left">Moneda</th>
                <th className="text-right">Ordenes</th>
                <th className="text-right">Total</th>
                <th className="text-right">Coins</th>
              </tr>
            </thead>
            <tbody>
              {by_method.map((r, i) => (
                <tr key={i} className="border-b border-[#3a3328]/40">
                  <td className="py-2 text-white">{MET[r.method] || r.method}</td>
                  <td className="text-[#b8b3a8]">{r.currency}</td>
                  <td className="text-right text-[#b8b3a8]">{r.orders}</td>
                  <td className="text-right text-white">{fmt(r.total, r.currency)}</td>
                  <td className="text-right text-amber-300">{Number(r.coins).toLocaleString()}</td>
                </tr>
              ))}
              {by_method.length === 0 && (
                <tr><td colSpan={5} className="text-center text-[#7e7768] py-4">Sin datos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
        <h3 className="text-sm text-[#d4a843] uppercase tracking-wider mb-3">Top 10 Donantes</h3>
        <div className="space-y-1.5">
          {top_donors.length === 0 && <div className="text-xs text-[#7e7768]">Sin datos</div>}
          {top_donors.map((d, i) => (
            <Link
              key={d.account}
              to={`/admin/player/${encodeURIComponent(d.account)}`}
              className="flex justify-between items-center text-sm py-2 px-2 -mx-2 rounded hover:bg-white/5"
              data-testid={`top-donor-${i}`}
            >
              <span className="text-white">
                <span className="text-[#7e7768] mr-2">#{i+1}</span>{d.account}
              </span>
              <span className="text-amber-300">{Number(d.coins).toLocaleString()} coins <span className="text-[#7e7768]">({d.orders} ord.)</span></span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
